<script setup lang="ts">
import { SubmissionsStatus } from "@profile-service-types/index.ts";
import { computed, ref, toRefs } from "vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import JsonTreeView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/JsonTreeView/index.vue";
import type {
  SubmissionFileOverride,
  SubmissionHistoryItem,
  SubmissionReadOverride,
  SubmissionsHistoryProps,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/types";
import {
  formatDate,
  getSubmissionDocuments,
  getSubmissionFileName,
  getSubmissionJsonData,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/utils.ts";
import ArrowDown from "@/components/shared/icons/ArrowDown.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import LockIcon from "@/components/shared/icons/LockIcon.vue";
import PaperclipIcon from "@/components/shared/icons/PaperclipIcon.vue";

const props = withDefaults(defineProps<SubmissionsHistoryProps>(), {
  history: () => [],
});

const { history } = toRefs(props);

const collapseInformationRef = ref(false);

const hasHistory = computed(() => history.value.length > 0);

const collapseInformation = (): void => {
  collapseInformationRef.value = !collapseInformationRef.value;
};

const emit =
  defineEmits<(e: "open-preview", submission: SubmissionReadOverride, file: SubmissionFileOverride) => void>();

const getSubmissionFile = (submission: SubmissionReadOverride): SubmissionFileOverride | null => {
  const file = (getSubmissionDocuments(submission?.submission_data) as SubmissionFileOverride[])?.[0];
  return file?.bucket && file?.key ? file : null;
};

// Each row previews its own version, never the most recent submission's.
const openPreview = (submission: SubmissionHistoryItem): void => {
  if (!submission.file) {
    return;
  }
  emit("open-preview", submission, submission.file);
};

const historyItems = computed<SubmissionHistoryItem[]>(() =>
  history.value.map((submission) => ({
    ...submission,
    file: getSubmissionFile(submission),
    jsonData: getSubmissionJsonData(submission?.submission_data) as Record<string, unknown>,
  })),
);
</script>

<template>
  <div
    v-if="hasHistory"
    class="bg-white shadow-md rounded-lg p-6 w-full text-indigo-base"
    data-testid="my-students-submission-history-section"
  >
    <div
      class="flex flex-row border-gray-200 cursor-pointer border-b-2 pb-2"
      data-testid="my-students-submission-history-header"
      @click.prevent="collapseInformation"
    >
      <div class="flex flex-row w-full items-center gap-3">
        <div class="flex items-center justify-center w-7 h-7">
          <slot name="icon-title">
            <ArrowDown
              v-if="collapseInformationRef"
              data-testid="my-students-submission-history-arrow-down"
            />
            <ArrowRight
              v-else
              class="w-5 h-5"
              data-testid="my-students-submission-history-arrow-right"
            />
          </slot>
        </div>
        <div class="flex items-center justify-center">
          <div class="font-bold text-base">
            <slot name="title">
              Previous Submissions ({{ history.length }})
            </slot>
          </div>
        </div>
      </div>
    </div>
    <Transition
      enter-active-class="transition-all duration-100 ease-out"
      enter-from-class="translate-y-1"
      enter-to-class="translate-y-0"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-1"
    >
      <div v-show="collapseInformationRef" class="flex flex-1 w-full pt-2">
        <slot name="layout">
          <div class="flex flex-1 flex-col w-full">
            <div
              v-for="(submission) in historyItems"
              :key="submission.id"
              class="grid grid-cols-[382px_1fr] items-start gap-x-10 gap-y-4 border-b border-gray-175 py-6 last:border-b-0 max-md:grid-cols-1"
            >
              <div class="flex flex-col items-start rounded-md bg-gray-125 p-4">
                <div
                  class="text-sm font-bold text-indigo-base"
                  data-testid="my-students-submission-history-date"
                >
                  {{ formatDate(submission?.created_at) }}
                </div>
                <div
                  v-if="submission?.form_version"
                  class="text-xs text-gray-800 font-semibold"
                  data-testid="my-students-submission-history-version"
                >
                  v{{ submission?.form_version }}
                </div>
                <button
                  v-if="submission.file"
                  type="button"
                  class="inline-flex items-center gap-1.5 mt-2.5 px-2.5 py-1.5 bg-white rounded border border-gray-175 text-xs font-semibold text-teal-900 transition-colors duration-150 hover:bg-teal-light"
                  data-testid="my-students-submission-history-file-name"
                  @click="openPreview(submission)"
                >
                  <PaperclipIcon class="w-3.5 h-3.5" />
                  {{ getSubmissionFileName(submission.file) }}
                </button>
                <span
                  v-else
                  class="inline-flex items-center gap-1.5 mt-2.5 px-2.5 py-1.5 bg-gray-125 rounded border border-gray-175 text-xs font-semibold text-gray-600"
                  data-testid="my-students-submission-history-file-unavailable"
                >
                  <LockIcon class="w-3.5 h-3.5" />
                  File unavailable
                </span>
              </div>

              <div class="flex flex-col gap-2.5 min-w-0">
                <div class="text-sm font-bold text-indigo-base">Feedback</div>
                <JSONtoHTML
                  v-if="submission?.feedback"
                  :tiptap-json="submission.feedback"
                  custom-class="text-13p leading-[1.55] text-indigo-base"
                  data-testid="my-students-submission-history-feedback-content"
                />
                <div class="flex flex-col md:flex-row justify-between gap-4 text-xs text-gray-700">
                  <span
                    v-if="submission?.reviewer_user_id"
                    data-testid="my-students-submission-history-feedback-reviewer"
                  >
                    Added by {{ submission?.reviewer?.first_name }} {{  submission?.reviewer?.last_name }} (<span class="text-teal-900">{{  submission?.reviewer?.email }}</span>)
                  </span>
                  <span data-testid="my-students-submission-history-feedback-date">
                    {{formatDate(submission?.reviewed_at)}}
                  </span>
                </div>
              </div>

              <JsonTreeView
                v-if="Object.keys(submission.jsonData).length > 0"
                :data="submission.jsonData"
                data-test-id="my-students-submission-history-jsontree"
                class="col-span-full w-[382px] max-w-full"
              />
            </div>
          </div>
        </slot>
      </div>
    </Transition>
  </div>
</template>
